import { cn } from '@foundation/utils/cn';
import { ComponentPropsWithoutRef, ElementType, ReactNode } from 'react';

/** Mirrors the text styles published in Figma (`Display/*`, `Heading/*`, `Body/*`, `Label/*`). */
export type TypographyVariant =
  | 'display-xl'
  | 'display-lg'
  | 'display-md'
  | 'heading-2xl'
  | 'heading-xl'
  | 'heading-lg'
  | 'heading-md'
  | 'heading-sm'
  | 'heading-xs'
  | 'body-2xl'
  | 'body-xl'
  | 'body-lg'
  | 'body-md'
  | 'body-sm'
  | 'label-md'
  | 'label-overline'
  | 'label-sm';

export type TypographyWeight = 'normal' | 'emphasis';

/** @deprecated Figma calls the regular weight `normal`. */
export type LegacyTypographyWeight = 'default';

export type TypographyColor =
  | 'primary'
  | 'secondary'
  | 'muted'
  | 'placeholder'
  | 'accent'
  | 'link'
  | 'on-color'
  | 'on-color-secondary'
  | 'error'
  | 'success'
  | 'warning'
  | 'info'
  | 'neutral';

export type TypographyProps = {
  readonly variant?: TypographyVariant;
  readonly color?: TypographyColor;
  /** Only applies to variants Figma publishes in both weights: body-*, label-md, label-sm */
  readonly weight?: TypographyWeight | LegacyTypographyWeight;
  readonly as?: ElementType;
  readonly children: ReactNode;
  readonly uppercase?: boolean;
  readonly capitalize?: boolean;
  readonly strikeThrough?: boolean;
};

const DEFAULT_ELEMENTS = {
  'display-xl': 'h1',
  'display-lg': 'h1',
  'display-md': 'h1',
  'heading-2xl': 'h1',
  'heading-xl': 'h1',
  'heading-lg': 'h2',
  'heading-md': 'h3',
  'heading-sm': 'h4',
  'heading-xs': 'h5',
  'body-2xl': 'p',
  'body-xl': 'p',
  'body-lg': 'p',
  'body-md': 'p',
  'body-sm': 'p',
  'label-md': 'span',
  'label-overline': 'span',
  'label-sm': 'span',
} as const satisfies Record<TypographyVariant, ElementType>;

// Variants Figma publishes as a `/default` + `/emphasis` pair.
const WEIGHTED_VARIANTS = new Set<TypographyVariant>([
  'body-2xl',
  'body-xl',
  'body-lg',
  'body-md',
  'body-sm',
  'label-md',
  'label-sm',
]);

// Figma's Typography collection has a Mobile mode that steps display and heading styles down one
// rung, so the base tier is the mobile value and the `md:` tier is the published desktop value.
const VARIANT_CLASSNAMES = {
  'display-xl':
    'font-heading font-strong tracking-pmui-tighter text-1200 leading-pmui-1000 md:text-1300 md:leading-pmui-1100',
  'display-lg':
    'font-heading font-strong tracking-pmui-tighter text-1100 leading-pmui-900 md:text-1200 md:leading-pmui-1000',
  'display-md':
    'font-heading font-strong tracking-pmui-tighter text-1000 leading-pmui-800 md:text-1100 md:leading-pmui-900',
  'heading-2xl':
    'font-heading font-strong tracking-pmui-base text-900 leading-pmui-700 md:text-1000 md:leading-pmui-800',
  'heading-xl':
    'font-heading font-strong tracking-pmui-base text-800 leading-pmui-600 md:text-900 md:leading-pmui-700',
  'heading-lg':
    'font-heading font-strong tracking-pmui-base text-700 leading-pmui-500 md:text-800 md:leading-pmui-600',
  'heading-md':
    'font-heading font-strong tracking-pmui-base text-600 leading-pmui-400 md:text-700 md:leading-pmui-500',
  'heading-sm':
    'font-heading font-strong tracking-pmui-base text-500 leading-pmui-400 md:text-600 md:leading-pmui-400',
  'heading-xs':
    'font-heading font-strong tracking-pmui-base text-500 leading-pmui-400',
  'body-2xl': 'font-body tracking-pmui-tighter text-800 leading-pmui-700',
  'body-xl': 'font-body tracking-pmui-tighter text-600 leading-pmui-500',
  'body-lg': 'font-body tracking-pmui-tighter text-500 leading-pmui-500',
  'body-md': 'font-body tracking-pmui-tighter text-400 leading-pmui-400',
  'body-sm': 'font-body tracking-pmui-tighter text-300 leading-pmui-300',
  // Figma sets Label/md/emphasis to a 12px line height; both weights use 16px here so
  // descenders are not clipped in a shared component.
  'label-md': 'font-body tracking-pmui-tighter text-200 leading-pmui-200',
  'label-overline':
    'uppercase font-body font-emphasis tracking-pmui-wide text-200 leading-pmui-100',
  'label-sm': 'font-body tracking-normal text-100 leading-pmui-200',
} as const satisfies Record<TypographyVariant, string>;

const WEIGHT_CLASSNAMES = {
  normal: 'font-normal',
  emphasis: 'font-emphasis',
} as const satisfies Record<TypographyWeight, string>;

const COLOR_CLASSNAMES = {
  primary: 'text-pmui-fg-primary',
  secondary: 'text-pmui-fg-secondary',
  muted: 'text-pmui-fg-muted',
  placeholder: 'text-pmui-fg-placeholder',
  accent: 'text-pmui-fg-brand-root',
  link: 'text-pmui-fg-link',
  'on-color': 'text-pmui-fg-inverse',
  'on-color-secondary': 'text-pmui-fg-inverse-secondary',
  error: 'text-pmui-fg-status-negative-default',
  success: 'text-pmui-fg-status-positive-default',
  warning: 'text-pmui-fg-status-warning-default',
  info: 'text-pmui-fg-status-info-default',
  neutral: 'text-pmui-fg-status-neutral-default',
} as const satisfies Record<TypographyColor, string>;

function TypographyComponent({
  variant = 'body-md',
  color = 'primary',
  weight = 'normal',
  uppercase = false,
  capitalize = false,
  strikeThrough = false,
  as,
  children,
  ...props
}: TypographyProps &
  Omit<ComponentPropsWithoutRef<'p'>, keyof TypographyProps | 'className'>) {
  const resolvedWeight = weight === 'default' ? 'normal' : weight;
  const Component = as ?? DEFAULT_ELEMENTS[variant];

  return (
    <Component
      className={cn(
        VARIANT_CLASSNAMES[variant],
        WEIGHTED_VARIANTS.has(variant) && WEIGHT_CLASSNAMES[resolvedWeight],
        COLOR_CLASSNAMES[color],
        uppercase && 'uppercase',
        capitalize && 'capitalize',
        strikeThrough && 'line-through',
      )}
      {...props}
    >
      {children}
    </Component>
  );
}

export const Typography = TypographyComponent;
